// Contact-request hooks (scout → athlete). Notifications are now created
// SERVER-SIDE by the backend routes — do not insert them from the client.
// Replaces src/utils/contactRequests.js.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../api/client";

// Latest request (any status) between the authed scout and one athlete.
export const useLatestContactRequest = (athleteId, options = {}) =>
  useQuery({
    queryKey: ["contact-requests", "latest", athleteId],
    queryFn: () =>
      apiFetch("/contact-requests", {
        query: { athlete_id: athleteId, sort: "created_at", order: "desc", pageSize: 1 },
      }),
    select: (data) => data?.items?.[0] ?? null,
    enabled: Boolean(athleteId) && (options.enabled ?? true),
  });

// { [athleteId]: latestRow } map for scout list hydration.
export const useScoutContactRequestsMap = (options = {}) =>
  useQuery({
    queryKey: ["contact-requests", "by-athlete-map"],
    queryFn: () => apiFetch("/contact-requests/by-athlete-map"),
    ...options,
  });

// Pending requests inbox for the authed athlete (rows embed `scout`). Includes
// a minor's `guardian_pending` requests, which only their guardian can answer.
export const usePendingContactRequests = (options = {}) =>
  useQuery({
    queryKey: ["contact-requests", "pending"],
    queryFn: () => apiFetch("/contact-requests/pending", { query: { include_guardian: 1 } }),
    select: (data) => data.items || [],
    ...options,
  });

// Sidebar badge count for the authed athlete.
export const usePendingContactCount = (options = {}) =>
  useQuery({
    queryKey: ["contact-requests", "pending-count"],
    queryFn: () => apiFetch("/contact-requests/pending-count"),
    select: (data) => data?.count ?? 0,
    refetchInterval: 60_000,
    ...options,
  });

// Scout sends a request. Backend notifies the athlete in-app.
export const useSubmitContactRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ athleteId, opportunityType, attentionReason, lookingFor, location, message }) =>
      apiFetch("/contact-requests", {
        method: "POST",
        body: {
          athlete_id: athleteId,
          opportunity_type: opportunityType,
          attention_reason: attentionReason,
          looking_for: lookingFor || null,
          location,
          message: message || null,
        },
      }),
    onSuccess: (row, variables) => {
      const athleteId = row?.athlete_id || variables.athleteId;
      queryClient.setQueryData(
        ["contact-requests", "by-athlete-map"],
        (previous = {}) => ({ ...previous, [athleteId]: row }),
      );
      queryClient.setQueryData(
        ["contact-requests", "latest", athleteId],
        { items: [row] },
      );
      queryClient.invalidateQueries({ queryKey: ["contact-requests"] });
    },
  });
};

// Minor asks the backend to email their guardian the review link again (and
// gives it a fresh 7-day window). Returns { sent, expires_at }.
export const useResendGuardianEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId }) =>
      apiFetch(`/contact-requests/${requestId}/resend-guardian-email`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-requests", "pending"] });
    },
  });
};

// Athlete approves/rejects. Idempotent server-side (pending-only transition);
// returns { updated: null } when it was already answered elsewhere.
export const useRespondToContactRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, decision }) =>
      apiFetch(`/contact-requests/${requestId}/respond`, { method: "POST", body: { decision } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-requests"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
};
